// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
// RxJS
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';

// Ngrx
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { AuthService, currentAuthToken, currentUser } from '../../auth';
import { environment } from '../../../../environments/environment';
import { uniqWith, isEqual } from 'lodash-es';
import * as moment from 'moment';
import {
  Task,
  TaskAttachment,
  TaskEntity,
  TaskStep,
  TaskStepActionExecutionLog,
  TaskTag,
} from '../../../views/pages/calendar/models';
import { FeatureFlag, FeatureFlagService } from '../../services/feature-flags/feature-flag.service';

@Injectable()
export class TaskService {
  private authToken;
  private companyId;
  private refreshVal = new Subject();
  private currentUser;
  private version = 'v3'; // default to v3
  userIdFilter = new Subject();
  formioStepValid = new BehaviorSubject<boolean>(false);
  newEntity = new BehaviorSubject<any | ''>('');

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
    private auth: AuthService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res.companyID;
      this.currentUser = res;
    });
    this.featureFlagService
      .getFlagValue(FeatureFlag.ENABLE_TIMELINE_TASK_CACHING)
      .subscribe((taskCachingEanbled) => {
        if (taskCachingEanbled === null) {
          return;
        }
        if (taskCachingEanbled) {
          this.version = 'v4';
        } else {
          this.version = 'v3';
        }
      });
  }

  private static getTaskPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.templatesIds?.length)
      filter.templatesIds.forEach((id) => (fromString += `&template_id=${id}`));
    if (filter.entitiesIds?.length)
      filter.entitiesIds.forEach((id) => (fromString += `&entity_id=${id}`));
    if (filter.createdDatetimeFrom)
      fromString += `&created_datetime_from=${filter.createdDatetimeFrom}`;
    if (filter.createdDatetimeTo) fromString += `&created_datetime_to=${filter.createdDatetimeTo}`;

    return fromString;
  }

  getTaskPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({ fromString: TaskService.getTaskPageQueryString(queryParams) });

    return queryParams.filter.templatesIds?.length
      ? this.http.get<QueryResultsModel>(
          `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks`,
          { params },
        )
      : of(new QueryResultsModel());
  }

  getTasksReportingList(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: TaskService.getTaskPageQueryString(queryParams) + `&all_tasks=true`,
    });

    return queryParams.filter.templatesIds?.length
      ? this.http.get<QueryResultsModel>(
          `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks`,
          { params },
        )
      : of(new QueryResultsModel());
  }

  // tslint:disable-next-line:max-line-length
  getTasks(
    page,
    size,
    parentId,
    entityId,
    userId,
    categoryId,
    templateId,
    tagId,
    priority,
    maxCreated,
    minCreated,
    maxDue,
    minDue,
    isFollowing = false,
    isNotAssigned = false,
    sort,
    direction,
    searchTerm = '',
    followup = null,
    status,
    officeId?,
  ): Observable<any> {
    let category = '';
    if (categoryId.length > 0) {
      if (categoryId[0] === 'without' || categoryId[0] === null) {
        category = '&without_category=true';
      } else {
        categoryId = uniqWith(categoryId, isEqual);
        categoryId.forEach((id) => (id ? (category += `&category_id=${id}`) : ''));
      }
    }
    let template = '';
    if (templateId === null) {
      template = '&without_template=true';
    } else if (templateId) {
      template = '&template_id=' + templateId;
    }
    let tag = '';
    if (tagId === null) {
      tag = '&without_tag=true';
    } else if (tagId) {
      tag = '&tag_id=' + tagId;
    }
    let entity = '';
    if (entityId[0] === -1 || !entityId) {
      entity = '&without_patient=true';
    } else {
      entityId = uniqWith(entityId, isEqual);
      entityId.forEach((id) => (id ? (entity += `&entity_id=${id}`) : ''));
    }
    let _priority = '';
    if (priority) {
      _priority = '&priority=' + priority;
    }
    let _status = '';
    if (status) {
      _status = '&progress=' + status;
    }
    let _followup = '';
    if (followup) {
      _followup = '&has_followup=' + followup;
    }
    let _user_following = '';
    if (isFollowing) {
      _user_following = '&user_following=' + isFollowing;
    }
    let _user_not_assigned = '';
    if (isNotAssigned) {
      _user_not_assigned = '&user_not_assigned=' + isNotAssigned;
    }
    if (!userId) {
      userId = '';
    }
    let dueDateMin = '';
    if (minDue && moment.isDate(minDue)) {
      dueDateMin = '&due_date_from=' + moment(minDue).format('YYYY-MM-DD');
    }
    let dueDateMax = '';
    if (maxDue && moment.isDate(maxDue)) {
      dueDateMax = '&due_date_to=' + moment(maxDue).format('YYYY-MM-DD');
    }
    let _responsible_user_id = '';
    if (userId) {
      _responsible_user_id = '&responsible_user_id=' + userId;
    }
    let _parentId = '';
    if (parentId) {
      _parentId = '&parent_id=' + parentId;
    }
    let createdDate = '';
    if (minCreated && moment.isDate(minCreated)) {
      createdDate += '&created_date_from=' + moment(minCreated).format('YYYY-MM-DD');
    }
    if (maxCreated && moment.isDate(maxCreated)) {
      createdDate += '&created_date_to=' + moment(maxCreated).format('YYYY-MM-DD');
    }

    const officeParam = officeId ? `&office_id=${officeId}` : '';
    const url =
      environment.TIMELINE_BASE +
      '/v3/companies/' +
      this.companyId +
      '/tasks?&sort=' +
      sort +
      ',' +
      direction +
      '&page=' +
      page +
      '&size=' +
      size +
      '&search_term=' +
      searchTerm +
      _parentId +
      entity +
      _responsible_user_id +
      category +
      template +
      tag +
      _priority +
      _status +
      _followup +
      createdDate +
      officeParam +
      dueDateMin +
      dueDateMax +
      _user_following +
      _user_not_assigned;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    return (templateId && templateId.constructor === Array && !templateId.length) ||
      this.currentUser.userType === 'Admin'
      ? of({ data: [], count: 0, page: 0, size: 10, total_pages: 0 })
      : this.http.get<any>(url, { headers: httpHeaders });
  }

  getTasksInFilteredView(
    isCompleted,
    isCanceled,
    page,
    size,
    parentId,
    entityId,
    userId,
    categoryId,
    entitiesId,
    templateId,
    tagId,
    priority,
    maxCreated,
    minCreated,
    maxDue,
    minDue,
    isFollowing,
    isNotAssigned,
    searchTerm = '',
    status,
    officeId?,
  ): Observable<any> {
    let entity = '';
    let Ids = '';
    let entityIDsVal = '';
    // without patient in filtered view
    if (entityId[0] === -1 && entitiesId.length > 0) {
      entitiesId.forEach((id) => (id ? (Ids += `&entity_id=${id}`) : ''));
      entity = `&without_patient=true${Ids}`;
      entityIDsVal = entitiesId.slice();
    }
    // all tasks in filtered view (no specific id for patient)
    else if (!entityId[0]) {
      entity = '';
      entityIDsVal = entitiesId.slice();
    }
    // specific patient Id in filter view
    else {
      entityId = uniqWith(entityId, isEqual);
      entityId.forEach((id) => (id ? (entity += `&entity_id=${id}`) : ''));
      entityIDsVal = entityId;
    }
    let _priority = '';
    if (priority) {
      _priority = '&priority=' + priority;
    }
    let _status = '';
    if (isCompleted && isCompleted !== '') {
      _status = '&progress=' + 'DONE';
    } else if (isCanceled) {
      _status = '&progress=' + 'CANCELED';
    } else {
      _status = '&progress=' + status;
    }
    let template = '';
    if (templateId) {
      template = '&template_id=' + templateId;
    }
    let _responsible_user_id = '';
    if (userId) {
      _responsible_user_id = '&responsible_user_id=' + userId;
    }
    let tag = '';
    if (tagId) {
      tag = '&tag_id=' + tagId;
    }
    let category = '';
    if (categoryId.length > 0) {
      if (categoryId[0] === 'without') {
        category = '&without_category=true';
      } else {
        categoryId = uniqWith(categoryId, isEqual);
        categoryId.forEach((id) => (id ? (category += `&category_id=${id}`) : ''));
      }
    }
    let dueDate = '';
    if (maxDue && minDue && moment.isDate(maxDue) && moment.isDate(minDue)) {
      dueDate =
        '&due_date_from=' +
        moment(minDue).format('YYYY-MM-DD') +
        '&due_date_to=' +
        moment(maxDue).format('YYYY-MM-DD');
    }
    let createdDate = '';
    if (minCreated && moment.isDate(minCreated)) {
      createdDate += '&created_date_from=' + moment(minCreated).format('YYYY-MM-DD');
    }
    if (maxCreated && moment.isDate(maxCreated)) {
      createdDate += '&created_date_to=' + moment(maxCreated).format('YYYY-MM-DD');
    }
    const body = {
      entity_id: entityIDsVal,
    };
    const officeParam = officeId ? `&office_id=${officeId}` : '';
    const url =
      environment.TIMELINE_BASE +
      '/v3/companies/' +
      this.companyId +
      '/tasks/filtered-view?&sort=dueDate,asc&page=' +
      page +
      '&size=' +
      size +
      '&parent_id=' +
      parentId +
      entity +
      category +
      _responsible_user_id +
      _priority +
      _status +
      template +
      tag +
      dueDate +
      createdDate +
      officeParam +
      '&user_following=' +
      isFollowing +
      '&user_not_assigned=' +
      isNotAssigned +
      '&search_term=' +
      searchTerm;

    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.currentUser.userType === 'Admin'
      ? of({
          data: [],
          count: 0,
          page: 0,
          size: 10,
          total_pages: 0,
        })
      : this.http.post<any>(url, body, { headers: httpHeaders });
  }

  getUserTasks(start, end, user_ids) {
    const url = user_ids
      ? `${environment.TIMELINE_BASE}/companies/${this.companyId}/tasks?from=${start}&to=${end}&user_ids=${user_ids[0]}`
      : `${environment.TIMELINE_BASE}/companies/${this.companyId}/tasks?from=${start}&to=${end}`;
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  createTask2(body): Observable<any> {
    const url = environment.TIMELINE_BASE + '/companies/' + this.companyId + '/tasks_v2';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }

  updateTask2(task): Observable<any> {
    const url = environment.TIMELINE_BASE + '/companies/' + this.companyId + '/tasks_v2/' + task.id;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put<any>(url, task, { headers: httpHeaders });
  }

  getEntities(entitiesIds: any[]) {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let ids = '';
    // tslint:disable-next-line:no-shadowed-variable
    entitiesIds = uniqWith(entitiesIds, isEqual);
    entitiesIds.forEach((element) => (ids = ids + '&id=' + element));
    const params = new HttpParams({ fromString: ids });
    return this.http.get<any>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/general_entities',
      {
        headers,
        params,
      },
    );
  }

  getUsersbyId(ids: any[]) {
    if (ids.length === 0) {
      return of({});
    }
    let param = '';
    const url = environment.USER_BASE + '/users-by-ids';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    ids = uniqWith(ids, isEqual);
    ids.forEach((element) => (param = param + '&id=' + element));
    const params = new HttpParams({ fromString: param });
    return this.http.get<any>(url, { headers: httpHeaders, params });
  }

  getTasksCounts(
    size: number,
    parentId: string,
    entityId,
    userId: string,
    categoryId,
    filteredView,
    entitiesId,
    templateId,
    tagId,
    priority,
    maxCreated,
    minCreated,
    maxDue,
    minDue,
    isFollowing,
    isNotAssigned,
    searchTerm = '',
    followup,
    status,
    officeId?,
  ): Observable<any> {
    let category = '';
    if (categoryId.length > 0) {
      if (categoryId[0] === 'without' || categoryId[0] === null) {
        category = '&without_category=true';
      } else {
        categoryId = uniqWith(categoryId, isEqual);
        categoryId.forEach((id) => (id ? (category += `&category_id=${id}`) : ''));
      }
    }
    let template = '';

    if (templateId === null) {
      template = '&without_template=true';
    } else if (templateId) {
      template = '&template_id=' + templateId;
    }
    let tag = '';
    if (tagId === null) {
      tag = '&without_tag=true';
    } else if (tagId) {
      tag = '&tag_id=' + tagId;
    }
    let entity = '';
    let Ids = '';
    let entityIDsVal = '';

    // without patient
    if (entityId[0] === -1 || !entityId) {
      // without patient & filtered view
      if (filteredView && entitiesId.length > 0) {
        entitiesId.forEach((id) => (id ? (Ids += `&entity_id=${id}`) : ''));
        entity = `&without_patient=true${Ids}`;
        entityIDsVal = entitiesId.slice();
        // without patient in normal view
      } else {
        entity = '&without_patient=true';
      }
    }
    // normal view with specific Id
    else {
      entityId = uniqWith(entityId, isEqual);
      entityId.forEach((id) => (id ? (entity += `&entity_id=${id}`) : ''));
      // filtered view specific id (1st condition) filtered view for all
      filteredView && !entityId[0]
        ? (entityIDsVal = entitiesId.slice())
        : (entityIDsVal = entityId);
    }
    let _priority = '';
    if (priority) {
      _priority = '&priority=' + priority;
    }
    let _status = '';
    if (status) {
      _status = '&progress=' + status;
    }
    let _followup = '';
    if (followup) {
      _followup = '&has_followup=' + followup;
    }
    let dueDateMin = '';
    if (minDue && moment.isDate(minDue)) {
      dueDateMin = '&due_date_from=' + moment(minDue).format('YYYY-MM-DD');
    }
    let dueDateMax = '';
    if (maxDue && moment.isDate(maxDue)) {
      dueDateMax = '&due_date_to=' + moment(maxDue).format('YYYY-MM-DD');
    }
    let createdDate = '';
    if (minCreated && moment.isDate(minCreated)) {
      createdDate += '&created_date_from=' + moment(minCreated).format('YYYY-MM-DD');
    }
    if (maxCreated && moment.isDate(maxCreated)) {
      createdDate += '&created_date_to=' + moment(maxCreated).format('YYYY-MM-DD');
    }
    const officeParam = officeId ? `&office_id=${officeId}` : '';
    const url =
      environment.TIMELINE_BASE +
      '/v3/companies/' +
      this.companyId +
      '/tasks/count' +
      (filteredView ? '/filtered-view' : '') +
      '?parent_id=' +
      parentId +
      entity +
      '&responsible_user_id=' +
      userId +
      category +
      template +
      tag +
      _priority +
      _status +
      _followup +
      dueDateMin +
      dueDateMax +
      createdDate +
      officeParam +
      '&user_following=' +
      isFollowing +
      '&user_not_assigned=' +
      isNotAssigned +
      '&search_term=' +
      searchTerm;

    const body = {
      entity_id: entityIDsVal,
    };
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    if (filteredView) {
      return this.currentUser.userType === 'Admin'
        ? of({ data: { open: 0, completed: 0, cancelled: 0 } })
        : this.http.post<any>(url, body, { headers: httpHeaders });
    }

    return (templateId && templateId.constructor === Array && !templateId.length) ||
      this.currentUser.userType === 'Admin'
      ? of({ data: { open: 0, completed: 0, cancelled: 0 } })
      : this.http.get<any>(url, { headers: httpHeaders });
  }

  getTasksCountsDashbord(userId, aDate, overdue?): Observable<any> {
    let date = '';
    if (aDate && overdue) {
      date = '&due_date_to=' + moment(aDate).format('YYYY-MM-DD');
    }
    if (aDate && !overdue) {
      date =
        '&due_date_from=' +
        moment(aDate).format('YYYY-MM-DD') +
        '&due_date_to=' +
        moment(aDate).format('YYYY-MM-DD');
    }
    const url =
      environment.TIMELINE_BASE +
      '/v3/companies/' +
      this.companyId +
      '/tasks/count?' +
      date +
      '&responsible_user_id=' +
      userId;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getTeamTaskPanelTable(date) {
    const url =
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/team?due_date_to=` +
      moment(date).format('YYYY-MM-DD');
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }

  getCommentHistoryByVersion(commentId: number, version: number) {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let commentHistoryUrl = `${environment.TIMELINE_BASE}/companies/${this.companyId}/tasks_v2/comments/history?id=${commentId}`;

    commentHistoryUrl =
      version > 0
        ? `${commentHistoryUrl}&versions=${version - 1}&versions=${version}`
        : `${commentHistoryUrl}&versions=${version}`;

    return this.http.get<any>(commentHistoryUrl, { headers });
  }

  refresh() {
    this.refreshVal.next(Math.random());
  }

  getRefresh() {
    return this.refreshVal.asObservable();
  }

  // for all categories
  getCategoriesForGroupTasks(categoryName: string, entityIds?: any) {
    let entity = '';
    if (entityIds) {
      entityIds.forEach((id) => (id ? (entity += `&entity_id=${id}`) : ''));
    }
    const url =
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/${categoryName}?` + entity;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.currentUser.userType === 'Admin'
      ? of({ data: [] })
      : this.http.get<any>(url, { headers });
  }

  // Task V3 APIs

  getTaskV3(id: number, isStepId?: boolean): Observable<{ data: Task }> {
    return this.http.get<{ data: Task }>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/${
        isStepId ? `steps/${id}` : id
      }`,
    );
  }

  getTaskV3History(taskId: number, stepId: number, version?: number): Observable<{ data: any[] }> {
    let params = '';

    if (version) params = `?versions=${version}${version > 0 ? `&versions=${version - 1}` : ''}`;

    return this.http.get<{ data: any[] }>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/${taskId}/steps/${stepId}/history${params}`,
    );
  }

  createTaskV3(task: Task): Observable<{ data: Task }> {
    return this.http.post<{ data: Task }>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks`,
      task,
    );
  }

  updateTaskV3Step(stepId: number, task: Task): Observable<{ data: Task }> {
    const body: {
      activity_id: number;
      activity_template_id: number;
      task_entities: Partial<TaskEntity>[];
      task_tags: TaskTag[];
      task_attachments: TaskAttachment[];
      step: TaskStep;
    } = {
      activity_id: task.activity_id,
      activity_template_id: task.activity_template_id,
      task_entities: task.task_entities,
      task_tags: task.task_tags,
      task_attachments: task.task_attachments,
      step: task.steps.find((s) => s.id === stepId),
    };

    return this.http.put<{ data: Task }>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/${task.id}/steps/${stepId}`,
      body,
    );
  }

  uploadAttachment(url: string, file: File): Observable<HttpResponse<void>> {
    return this.http.put<void>(url, file, { observe: 'response' });
  }

  addTaskAttachments(
    taskId: number,
    attachmentsObj: { task_attachments: TaskAttachment[] },
  ): Observable<{ data: TaskAttachment[] }> {
    return this.http.post<{ data: TaskAttachment[] }>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/${taskId}/attachments`,
      attachmentsObj,
    );
  }

  shareTaskAttachments(taskId: number, data): Observable<HttpResponse<void>> {
    return this.http.post<void>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/${taskId}/attachments/share`,
      data,
      {
        observe: 'response',
      },
    );
  }

  updateTaskLock(taskId: number, lockObj: { lock: boolean }): Observable<HttpResponse<void>> {
    return this.http.put<void>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.companyId}/tasks/${taskId}/lock_unlock`,
      lockObj,
      {
        observe: 'response',
      },
    );
  }
  setFormValidity(isValid: any) {
    this.formioStepValid.next(isValid);
  }

  checkFormValidity() {
    return this.formioStepValid;
  }

  getTaskStepActionExecutionLogs(taskStepId: number): Observable<{ data: TaskStepActionExecutionLog[] }> {
    const params = new HttpParams({ fromString: `resourceType=TASK_STEP&resourceIdEq=${taskStepId}&page=0&size=20` });

    return this.http.get<{ data: TaskStepActionExecutionLog[] }>(`${environment.NOTIFICATION_BASE}/api/v1/action-executions`, { params });
  }

  retryTaskStepActionExecution(actionExecutionId: number): Observable<TaskStepActionExecutionLog> {
    return this.http.post<TaskStepActionExecutionLog>(`${environment.NOTIFICATION_BASE}/api/v1/action-executions/${actionExecutionId}/retry`, null);
  }
}
